import { AddIcon } from "@chakra-ui/icons"
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react"
import { Api } from "../lib/api"
import React from "react"
import Link from "./Link"
import { FaDownload } from "react-icons/fa6"
import { addAlert, AlertType } from "@/lib/alerts"
import { Paginator } from "./Paginator"

const Requests = () => {
  const api = new Api()
  const [hasStatus, setHasStatus] = React.useState(false)
  const [requests, setRequests] = React.useState([])
  const [offset, setOffset] = React.useState(0)
  const [limit] = React.useState(50)
  const [total, setTotal] = React.useState(0)

  const getRequests = async () => {
    try {
      console.log("Getting requests with offset:", offset, "and limit:", limit)
      const requestsList = await api.requestsList(offset, limit)
      console.log("Raw requestsList response:", requestsList)
      if (Array.isArray(requestsList)) {
        setRequests(requestsList)
        setTotal(requestsList.count)
        console.log("Total requests:", requestsList.length)
      } else if (requestsList && requestsList.results) {
        setRequests(requestsList.results)
        setTotal(requestsList.count || 0)
      } else {
        console.error("Invalid requestsList response:", requestsList)
        setRequests([])
        setTotal(0)
      }
    } catch (error) {
      console.error("Error fetching requests:", error)
      addAlert(AlertType.ERROR, "Error fetching requests", error)
      setRequests([])
      setTotal(0)
    }
  }

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const status = await api.statusRetrieve()
        setHasStatus(status)
        await getRequests()
      } catch (error) {
        console.error("Error in fetchData:", error)
        setHasStatus(false)
      }
    }

    fetchData()
  }, [offset, limit])

  const hasProxy = React.useMemo(() => {
    if (!hasStatus) return
    return hasStatus.behind_proxy
  }, [hasStatus])

  const handleFulfillRequests = async () => {
    try {
      await api.requestsFulfill()
      addAlert(
        AlertType.SUCCESS,
        "Requests fulfilled",
        "Requests fulfilled successfully",
      )
      getRequests()
    } catch (error) {
      const err = await error.json()
      console.log("Error creating identity", err)
      addAlert(AlertType.ERROR, "Error creating identity", err.error)
    }
  }

  const requestList = () => {
    console.log("Current requests:", requests)

    return (
      <TableContainer
        boxShadow="md"
        borderRadius="0.75em"
        overflowY="auto"
        height="100%"
        bg="white"
      >
        <Table>
          <Thead>
            <Tr>
              <Th>Request ID</Th>
              <Th>Created</Th>
              <Th>Schema</Th>
              <Th>Requestor</Th>
              <Th>Field</Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests &&
              requests.map((request) => (
                <Tr key={request.id}>
                  <Td>
                    <div>
                      <span>{request.id}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{request.created}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{request.schema.name}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{request.identity.user.email}</span>
                    </div>
                  </Td>
                  <Td>
                    <div>
                      <span>{request.field_name}</span>
                    </div>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </TableContainer>
    )
  }

  const hasProxyNoRequests = () => {
    if (!requests || requests.length === 0) {
      return (
        <Alert status="info" position="relative" width="100%" height="auto">
          <HStack>
            <AlertIcon />
            <Heading size="sm" mt={0} mb={0}>
              You have no outstanding requests
            </Heading>
          </HStack>
        </Alert>
      )
    }
    return null
  }

  const noProxyHasRequests = () => {
    if (!requests || requests.length === 0) {
      return (
        <Alert status="info" position="relative" width="100%" height="auto">
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <HStack>
              <AlertIcon />
              <Heading size="sm" mt={0} mb={0}>
                No Proxy Detected
              </Heading>
            </HStack>
            <Text whiteSpace="normal" overflowWrap="break-word">
              Download the Blind Proxy to create accept requests.
            </Text>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Alert>
      )
    }
    return null
  }

  const noProxyEmpty = () => {
    return (
      <>
        <Alert status="info" position="relative" width="100%" height="auto">
          <VStack
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
          >
            <HStack>
              <AlertIcon />
              <Heading size="md" mt={0} mb={0}>
                No Proxy Detected
              </Heading>
            </HStack>
            <VStack>
              <Text
                variant="base"
                width="100%"
                whiteSpace="normal"
                overflowWrap="break-word"
              >
                Only encrypted data ever enters or exits Blind Insight, and we
                don&apos;t hold any keys. <br></br>Download the Blind Proxy to
                create accept key-sharing requests.
              </Text>
            </VStack>
            <Flex
              width="full"
              direction={{ base: "column", md: "row" }}
              justifyContent="right"
              gap={{ base: "1", md: "4" }}
              flexWrap="wrap"
            >
              <Link
                href="https://docs.blindinsight.io/download/"
                isExternal
                rel="noreferrer"
              >
                <Button
                  variant="primary"
                  title="Download the Blind Proxy"
                  size={{ base: "sm", md: "md" }}
                  rightIcon={<FaDownload />}
                >
                  Download the Blind Proxy
                </Button>
              </Link>
            </Flex>
          </VStack>
        </Alert>
      </>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      gap="1em"
      bg="Gray.100"
      p={4}
      borderRadius="1em"
      height={{ base: "50%", md: "100%" }}
    >
      <HStack
        justifyContent="space-between"
        alignItems={hasProxy ? "center" : "flex-start"}
      >
        {hasProxy && (!requests || requests.length === 0) ? (
          hasProxyNoRequests()
        ) : !hasProxy && requests && requests.length > 0 ? (
          noProxyHasRequests()
        ) : (
          <span />
        )}
        <Button
          aria-label={
            hasProxy
              ? "Create a new Identity"
              : "You must be behind the proxy to create a new Identity"
          }
          disabled={!hasProxy || requests.count === 0}
          rightIcon={<AddIcon />}
          variant="primary"
          onClick={handleFulfillRequests}
          flexShrink={0}
          minW="fit-content"
        >
          Fulfill Requests
        </Button>
      </HStack>
      {!hasProxy && (!requests || requests.length === 0)
        ? noProxyEmpty()
        : null}
      <Paginator
        offset={offset}
        total={total}
        limit={limit}
        setOffset={setOffset}
      />
      {requestList()}
    </Box>
  )
}

export default Requests
